import {
  Button,
  DataTable,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import type { DataTableColumn } from '@noorden-insurtech/alba-design-system';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { QuoteActivity } from '@/services/quotes/activities';
import { useIsInsurerUser } from '@/shared/hooks/use-is-insurer-user.hook';
import { formatDateTimeShortToBrazil } from '@/shared/utils/format-date.util';
import { useQuoteActivities } from './hooks/use-quote-activities.hook';
import { QuoteActivityDetailDialog } from './quote-activity-detail-dialog.component';
import { getQuoteActivityColor } from './quote-activity.config';

const SECTION_CLASS =
  'flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6';

interface QuoteActivitiesSectionProps {
  policyId: string;
}

/**
 * Bloco ATIVIDADES da tela de análise: trilha de auditoria da cotação.
 *
 * Compartilhado entre as telas de análise dos produtos — recebe só o
 * `policyId`. Hoje apenas o PME grava atividades, mas nada aqui é específico
 * de produto (ver `Portal.Api/docs/quote-activities.md`).
 *
 * Some para quem não é da seguradora. O gate REAL é o backend, que devolve
 * 403 para corretor e assessoria; este é defesa em profundidade para quando o
 * bloco for reaproveitado fora da rota de análise.
 *
 * Usa `useIsInsurerUser` (qualquer perfil da seguradora) e NÃO
 * `useIsUnderwriterUser`: a rota de análise deixa o administrador entrar e o
 * backend também o autoriza — exigir `insurer.underwriter` aqui esconderia o
 * bloco dele sem motivo.
 */
export function QuoteActivitiesSection({
  policyId,
}: QuoteActivitiesSectionProps) {
  const { isInsurerUser, isLoading: isLoadingProfile } = useIsInsurerUser();
  const [selected, setSelected] = useState<QuoteActivity | null>(null);

  const isAllowed = isInsurerUser === true;
  const query = useQuoteActivities(policyId, { enabled: isAllowed });

  const activities = useMemo(
    () => query.data?.pages.flatMap(page => page.items) ?? [],
    [query.data]
  );

  const columns = useMemo<DataTableColumn<QuoteActivity>[]>(
    () => [
      {
        accessorKey: 'occurredAt',
        header: 'DATA',
        size: 150,
        cell: ({ row }) => (
          <span className='text-sm text-alba-black-500'>
            {formatDateTimeShortToBrazil(row.original.occurredAt)}
          </span>
        ),
      },
      {
        accessorKey: 'event',
        header: 'EVENTO',
        size: 190,
        cell: ({ row }) => (
          <Tag.Root
            variant='solid'
            colorScheme={getQuoteActivityColor(row.original.event)}
            size='sm'
          >
            <Tag.Label>{row.original.eventLabel}</Tag.Label>
          </Tag.Root>
        ),
      },
      {
        accessorKey: 'subject',
        header: 'ASSUNTO',
        size: 280,
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <span className='text-sm text-alba-black-500'>
              {row.original.subjectLabel}
            </span>
            {row.original.description && (
              <span
                className='block max-w-[280px] truncate text-xs text-alba-gray-500'
                title={row.original.description}
              >
                {row.original.description}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'actorName',
        header: 'USUÁRIO',
        size: 200,
        cell: ({ row }) => (
          <span className='text-sm text-alba-black-500'>
            {row.original.actorName ?? '—'}
          </span>
        ),
      },
      {
        id: 'details',
        header: '',
        size: 60,
        cell: ({ row }) => {
          const hasDetail =
            row.original.changes.length > 0 || Boolean(row.original.description);

          return (
            <button
              type='button'
              aria-label='Ver detalhe da atividade'
              disabled={!hasDetail}
              onClick={() => setSelected(row.original)}
              className='rounded-md p-1 text-alba-gray-500 transition-colors hover:text-alba-blue-700 disabled:cursor-not-allowed disabled:opacity-30'
            >
              <Search className='h-4 w-4' />
            </button>
          );
        },
      },
    ],
    []
  );

  if (isLoadingProfile || !isAllowed) {
    return null;
  }

  return (
    <div className={SECTION_CLASS}>
      <Text className='text-base text-alba-black-500'>Atividades</Text>

      {query.isError ? (
        <Text className='text-sm text-alba-gray-500'>
          Não foi possível carregar o histórico de atividades desta cotação.
        </Text>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={activities}
            loading={query.isLoading}
            pageSize={10}
            emptyMessage='Nenhuma atividade registrada para esta cotação.'
          />

          {query.hasNextPage && (
            <div className='flex justify-center'>
              <Button
                variant='secondary'
                size='sm'
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                Carregar mais
              </Button>
            </div>
          )}
        </>
      )}

      <QuoteActivityDetailDialog
        activity={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
